import { Box, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { currencyFormat } from "../../utils";
import classes from "../../pages/customer-billing/BillingDetails.module.css";

export type OldestInvoice = {
  refCode: string;
  days: number;
  amount: number;
} | null;

interface Props {
  metrics: {
    dso: number;
    currentBalance: number;
    overdueBalance: number;
    onTimePaymentRate: number;
    openInvoicesCount: number;
    overdueInvoicesCount: number;
  } | null;
  oldest: OldestInvoice;
}

type Cell = {
  key: string;
  label: string;
  value: string;
  unit?: string;
  hint?: string;
  danger?: boolean;
};

export function ARMetricsCards({ metrics, oldest }: Props) {
  const { t } = useTranslation("billing");

  if (!metrics) return null;

  const overduePct =
    metrics.currentBalance > 0
      ? Math.round((metrics.overdueBalance / metrics.currentBalance) * 1000) / 10
      : 0;

  const cells: (Cell | null)[] = [
    {
      key: "balance",
      label: t("ar.totalBalance"),
      value: currencyFormat(metrics.currentBalance, { withSymbol: false }),
    },
    {
      key: "overdue",
      label: t("ar.overdue"),
      value: currencyFormat(metrics.overdueBalance, { withSymbol: false }),
      danger: metrics.overdueBalance > 0,
      hint: t("ar.overdueHint", {
        count: metrics.overdueInvoicesCount,
        pct: overduePct,
      }),
    },
    {
      key: "dso",
      label: t("ar.dso"),
      value: String(metrics.dso),
      unit: t("ar.daysUnit"),
      hint: t("ar.dsoHint"),
    },
    {
      key: "onTime",
      label: t("ar.onTime"),
      value: String(metrics.onTimePaymentRate),
      unit: "%",
      hint: t("ar.last12Months"),
    },
    oldest
      ? {
          key: "oldest",
          label: t("ar.oldest"),
          value: String(oldest.days),
          unit: t("ar.daysUnit"),
          hint: t("ar.oldestHint", {
            inv: oldest.refCode,
            amount: currencyFormat(oldest.amount, { withSymbol: false }),
          }),
        }
      : null,
  ];

  const visible = cells.filter(Boolean) as Cell[];
  if (visible.length === 0) return null;

  return (
    <Box
      className={classes.kpiCard}
      style={{ gridTemplateColumns: `repeat(${visible.length}, minmax(0, 1fr))` }}
    >
      {visible.map((cell) => (
        <Box key={cell.key} className={classes.kpiCell}>
          <Text className={classes.kpiLabel}>{cell.label}</Text>
          <Box className={classes.kpiValueRow}>
            <Text
              component="span"
              className={`${classes.kpiValue} ${cell.danger ? classes.kpiValueDanger : ""}`}
            >
              {cell.value}
            </Text>
            {cell.unit ? (
              <Text component="span" className={classes.kpiUnit}>
                {cell.unit}
              </Text>
            ) : null}
          </Box>
          {cell.hint ? <Text className={classes.kpiHint}>{cell.hint}</Text> : null}
        </Box>
      ))}
    </Box>
  );
}
